import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { styled } from 'styled-components';

import { Box } from '#/components/Box/Box';
import { Button } from '#/components/Button/Button';
import { Hide } from '#/components/Hide/Hide';
import { IdealistLogo } from '#/components/Logo/IdealistLogo';
import { H1 } from '#/components/Text/H1';
import { FlashMessageContainer } from '#/containers/FlashMessageContainer';
import { ShowConfirmationModal } from '#/containers/ShowConfirmationModal';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import { homeRoute } from '#/routing/routes';
import { colors, gutterWidth } from '#/theme/theme';
import { trackAnchorClicked } from '#/utils/analytics/track/trackAnchorClicked';
import { getItem as getLocalStorageItem } from '#/utils/localStorage';
import { hideModal } from '#/zustand-stores/modalStore';

const testMode = getLocalStorageItem('testMode') as boolean;

type Props = {
  submitDraft?: () => Promise<void>;
  otherLinks?: ReactNode[];
  edited?: boolean;
  trackingTitle?: string;
  title?: string;
  children?: ReactNode;
  isDraft?: boolean;
  cartDropdownButton?: ReactNode;
};

function CloseButton({
  edited,
  submitDraft,
  isDraft,
}: {
  edited: boolean;
  submitDraft?: () => Promise<void>;
  isDraft?: boolean;
}) {
  const { i18n, locale } = useI18n();
  const { user } = useUser();

  const navigate = useNavigate();

  function handleCloseForm() {
    hideModal();
    const orgCount = user?.orgs?.length ?? 0;

    if (orgCount === 0) {
      void navigate(`/${locale}`);
    } else if (orgCount === 1) {
      const orgId = user?.orgs?.[0]?.id;
      void navigate(`/admin/${orgId}`);
    } else {
      void navigate('/select-org');
    }
  }

  if (submitDraft && isDraft && edited) {
    return (
      <ShowConfirmationModal
        qaId="multi-step-save-draft-modal"
        type="warning"
        title={i18n('Do you want to save your draft?')}
        buttonText={i18n('Save Draft')}
        extraButtons={
          <Button
            type="button"
            variant="danger"
            onClick={() => {
              handleCloseForm();
            }}
            qaId="discard-button"
          >
            {i18n('Continue Without Saving')}
          </Button>
        }
        onConfirm={submitDraft}
        render={({ showModal }) => (
          <Button
            type="button"
            onClick={showModal}
            title={i18n('Cancel')}
            qaId="multi-form-close"
            tabIndex={0}
            icon={{ type: 'normalized', name: 'close' }}
            variant="tertiary-grey"
          />
        )}
      />
    );
  }

  return (
    <ShowConfirmationModal
      qaId="multi-step-close-modal"
      type="danger"
      title={i18n('Are you sure you want to go back?')}
      buttonText={i18n('Yes, go back')}
      body={i18n('If you go back, your data will not be saved.')}
      onConfirm={() => Promise.resolve(handleCloseForm())}
      render={({ showModal }) => (
        <Button
          type="button"
          onClick={() => (edited ? showModal() : handleCloseForm())}
          title={i18n('Cancel')}
          qaId="multi-form-close"
          icon={{ type: 'normalized', name: 'close' }}
          variant="tertiary-grey"
          tabIndex={0}
        />
      )}
    />
  );
}

const HeaderRow = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 76px;
  padding: 0 ${gutterWidth / 2}px;
  border-bottom: 1px solid #eaeaea;
`;

export function MultiStepFormBaseHeader({
  submitDraft,
  otherLinks,
  edited,
  trackingTitle,
  title,
  isDraft,
  children,
  cartDropdownButton,
}: Props) {
  const { locale } = useI18n();
  return (
    <Box
      position={testMode ? 'relative' : 'fixed'}
      top={0}
      left={0}
      width="100%"
      bg="white"
      zIndex={100}
    >
      <HeaderRow>
        <Link
          to={homeRoute.with({ lang: locale })}
          data-qa-id="header-logo-link"
          onClick={(event) =>
            trackAnchorClicked(
              event,
              `Idealist Logo Header - ${
                title ? `${trackingTitle}` : 'Multi Step Form'
              }`,
            )
          }
        >
          <IdealistLogo withText height={42} />
        </Link>

        {title && (
          <Hide xs sm>
            <H1 styleAs="h3" m={0}>
              {title}
            </H1>
          </Hide>
        )}

        <div style={{ display: 'flex', alignItems: 'center' }}>
          {cartDropdownButton}

          <CloseButton
            submitDraft={submitDraft}
            edited={Boolean(edited)}
            isDraft={isDraft}
          />
        </div>
      </HeaderRow>

      {otherLinks && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width={1}
          borderBottom="1px solid #eaeaea"
        >
          {otherLinks.map((link, index) => (
            <Box // oxlint-disable-next-line react/no-array-index-key
              key={index}
              px={32}
              height={52}
              display="flex"
              justifyContent="center"
              alignItems="center"
              // @ts-expect-error TS(2322): Type 'string | false' is not assignable to type 'R... Remove this comment to see the full error message
              border={index > 0 && `1px solid ${colors.selectionGrey}`}
            >
              {link}
            </Box>
          ))}
        </Box>
      )}

      <FlashMessageContainer />

      {children}
    </Box>
  );
}
